import { useState, useEffect, useRef } from 'react'
import { Device, Call } from '@twilio/voice-sdk'
import { Phone, PhoneOff, X, Minimize2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useAuth } from '@/global/auth/AuthContext'
import { callFunction } from '@/lib/http'
import { activities } from '@/lib/http'

interface SoftphoneProps {
  initialNumber?: string
  onClose?: () => void
  subjectType?: 'agent' | 'client' | 'organization'
  subjectId?: string
}

export function Softphone({ initialNumber = '', onClose, subjectType, subjectId }: SoftphoneProps) {
  const { user, activeTenantId } = useAuth()
  const [device, setDevice] = useState<Device | null>(null)
  const [call, setCall] = useState<Call | null>(null)
  const [phoneNumber, setPhoneNumber] = useState(initialNumber)
  const [status, setStatus] = useState<'idle' | 'connecting' | 'ringing' | 'active' | 'error'>('idle')
  const [error, setError] = useState<string | null>(null)
  const [isMinimized, setIsMinimized] = useState(false)
  const [callDuration, setCallDuration] = useState(0)
  const deviceRef = useRef<Device | null>(null)
  const callTimerRef = useRef<NodeJS.Timeout | null>(null)
  const callStartTimeRef = useRef<number | null>(null)

  // Initialize Twilio Device
  useEffect(() => {
    if (!user || !activeTenantId) return

    const initDevice = async () => {
      try {
        // Get access token from Edge Function
        const response = await callFunction<{ token: string }>('twilio_generate_token', {})
        
        if (!response.token) {
          throw new Error('Failed to get access token')
        }

        // Create and register device
        const newDevice = new Device(response.token, {
          logLevel: 1, // Debug level
        })

        // Device event handlers
        newDevice.on('registered', () => {
          // Device registered
        })

        newDevice.on('error', (error) => {
          setStatus('error')
          setError(error.message || 'Device error occurred')
        })

        newDevice.on('tokenWillExpire', async () => {
          try {
            const refreshResponse = await callFunction<{ token: string }>('twilio_generate_token', {})
            if (refreshResponse.token) {
              newDevice.updateToken(refreshResponse.token)
            }
          } catch (err) {
            // Token refresh failed
          }
        })

        await newDevice.register()
        setDevice(newDevice)
        deviceRef.current = newDevice
      } catch (err: any) {
        setStatus('error')
        setError(err?.message || err?.toString() || 'Failed to initialize phone')
      }
    }

    initDevice()

    // Cleanup on unmount
    return () => {
      if (deviceRef.current) {
        deviceRef.current.destroy()
      }
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current)
      }
    }
  }, [user, activeTenantId])

  // Start call timer
  const startCallTimer = () => {
    setCallDuration(0)
    callTimerRef.current = setInterval(() => {
      setCallDuration((prev) => prev + 1)
    }, 1000)
  }

  // Stop call timer
  const stopCallTimer = () => {
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current)
      callTimerRef.current = null
    }
  }

  // Make outgoing call
  const makeCall = async () => {
    if (!device || !phoneNumber) return

    try {
      setStatus('connecting')
      setError(null)

      // Log call initiation
      if (activeTenantId && user?.id) {
        activities.log({
          tenantId: activeTenantId,
          actor_user_id: user.id,
          action: 'call_initiated',
          subject_type: subjectType || 'unknown',
          subject_id: subjectId || activeTenantId,
          message: `Call initiated to ${phoneNumber}`,
          data: {
            phone_number: phoneNumber,
            direction: 'outbound',
            timestamp: new Date().toISOString(),
          },
        })
      }

      const outgoingCall = await device.connect({
        params: {
          To: phoneNumber,
        },
      })

      // Call event handlers
      outgoingCall.on('accept', () => {
        setStatus('active')
        setCall(outgoingCall)
        startCallTimer()
        callStartTimeRef.current = Date.now()

        // Log call connected
        if (activeTenantId && user?.id) {
          activities.log({
            tenantId: activeTenantId,
            actor_user_id: user.id,
            action: 'call_connected',
            subject_type: subjectType || 'unknown',
            subject_id: subjectId || activeTenantId,
            message: `Call connected with ${phoneNumber}`,
            data: {
              phone_number: phoneNumber,
              direction: 'outbound',
              timestamp: new Date().toISOString(),
            },
          })
        }
      })

      outgoingCall.on('disconnect', () => {
        const duration = callStartTimeRef.current ? Math.floor((Date.now() - callStartTimeRef.current) / 1000) : 0
        
        // Log call ended
        if (activeTenantId && user?.id) {
          activities.log({
            tenantId: activeTenantId,
            actor_user_id: user.id,
            action: 'call_ended',
            subject_type: subjectType || 'unknown',
            subject_id: subjectId || activeTenantId,
            message: `Call ended with ${phoneNumber}`,
            data: {
              phone_number: phoneNumber,
              duration_seconds: duration,
              direction: 'outbound',
              timestamp: new Date().toISOString(),
            },
          })
        }

        setStatus('idle')
        setCall(null)
        stopCallTimer()
        callStartTimeRef.current = null
      })

      outgoingCall.on('cancel', () => {
        // Log call cancelled
        if (activeTenantId && user?.id) {
          activities.log({
            tenantId: activeTenantId,
            actor_user_id: user.id,
            action: 'call_cancelled',
            subject_type: subjectType || 'unknown',
            subject_id: subjectId || activeTenantId,
            message: `Call cancelled to ${phoneNumber}`,
            data: {
              phone_number: phoneNumber,
              direction: 'outbound',
              timestamp: new Date().toISOString(),
            },
          })
        }

        setStatus('idle')
        setCall(null)
        stopCallTimer()
        callStartTimeRef.current = null
      })

      outgoingCall.on('reject', () => {
        // Log call rejected
        if (activeTenantId && user?.id) {
          activities.log({
            tenantId: activeTenantId,
            actor_user_id: user.id,
            action: 'call_rejected',
            subject_type: subjectType || 'unknown',
            subject_id: subjectId || activeTenantId,
            message: `Call rejected by ${phoneNumber}`,
            data: {
              phone_number: phoneNumber,
              direction: 'outbound',
              timestamp: new Date().toISOString(),
            },
          })
        }

        setStatus('idle')
        setCall(null)
        setError('Call was rejected')
        stopCallTimer()
        callStartTimeRef.current = null
      })

      outgoingCall.on('error', (error) => {
        // Log call error
        if (activeTenantId && user?.id) {
          activities.log({
            tenantId: activeTenantId,
            actor_user_id: user.id,
            action: 'call_failed',
            subject_type: subjectType || 'unknown',
            subject_id: subjectId || activeTenantId,
            message: `Call failed to ${phoneNumber}`,
            data: {
              phone_number: phoneNumber,
              error: error.message || 'Unknown error',
              direction: 'outbound',
              timestamp: new Date().toISOString(),
            },
          })
        }

        setStatus('error')
        setError(error.message || 'Call error occurred')
        setCall(null)
        stopCallTimer()
        callStartTimeRef.current = null
      })

      setStatus('ringing')
    } catch (err: any) {
      // Log call error
      if (activeTenantId && user?.id) {
        activities.log({
          tenantId: activeTenantId,
          actor_user_id: user.id,
          action: 'call_failed',
          subject_type: subjectType || 'unknown',
          subject_id: subjectId || activeTenantId,
          message: `Failed to initiate call to ${phoneNumber}`,
          data: {
            phone_number: phoneNumber,
            error: err.message || 'Unknown error',
            direction: 'outbound',
            timestamp: new Date().toISOString(),
          },
        })
      }

      setStatus('error')
      setError(err.message || 'Failed to make call')
      stopCallTimer()
      callStartTimeRef.current = null
    }
  }

  // Hang up call
  const hangUp = () => {
    if (call) {
      call.disconnect()
      setCall(null)
      setStatus('idle')
      stopCallTimer()
    }
  }

  // Format call duration
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  // Handle number pad input
  const handleDigit = (digit: string) => {
    setPhoneNumber((prev) => prev + digit)
  }

  // Handle backspace
  const handleBackspace = () => {
    setPhoneNumber((prev) => prev.slice(0, -1))
  }

  // Render status text
  const getStatusText = () => {
    switch (status) {
      case 'connecting':
        return 'Connecting...'
      case 'ringing':
        return 'Ringing...'
      case 'active':
        return formatDuration(callDuration)
      case 'error':
        return error || 'Error'
      default:
        return 'Ready'
    }
  }

  // Don't render if minimized and no active call
  if (isMinimized && status === 'idle') {
    return null
  }

  return (
    <Card className="fixed bottom-4 right-4 w-80 shadow-xl z-50 glass">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Phone className="h-5 w-5" />
            Phone
          </CardTitle>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setIsMinimized(!isMinimized)}
            >
              <Minimize2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      {!isMinimized && (
        <CardContent className="space-y-4">
          {/* Status */}
          <div className="text-center">
            <p className="text-sm text-muted-foreground">{getStatusText()}</p>
            {error && (
              <p className="text-xs text-destructive mt-1">{error}</p>
            )}
          </div>

          {/* Phone Number Input */}
          {status === 'idle' && (
            <>
              <Input
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="text-center text-lg"
              />

              {/* Dialpad */}
              <div className="grid grid-cols-3 gap-2">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map((digit) => (
                  <Button
                    key={digit}
                    variant="outline"
                    size="lg"
                    onClick={() => handleDigit(digit)}
                    className="h-12 text-lg font-semibold"
                  >
                    {digit}
                  </Button>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={handleBackspace}
                  disabled={!phoneNumber}
                >
                  Delete
                </Button>
                <Button
                  onClick={makeCall}
                  disabled={!phoneNumber || !device}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Phone className="h-4 w-4 mr-2" />
                  Call
                </Button>
              </div>
            </>
          )}

          {/* Active Call Controls */}
          {(status === 'connecting' || status === 'ringing' || status === 'active') && (
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-lg font-semibold">{phoneNumber}</p>
              </div>

              <Button
                onClick={hangUp}
                variant="destructive"
                size="lg"
                className="w-full"
              >
                <PhoneOff className="h-5 w-5 mr-2" />
                Hang Up
              </Button>
            </div>
          )}

          {/* Device not ready */}
          {!device && status !== 'error' && (
            <div className="text-center text-sm text-muted-foreground">
              <p>Initializing phone...</p>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  )
}
